import { TAddon, TDuration, TPlanSummary } from 'common/cms-types'
import { AddonSlug } from 'common/planSlugs'
import { planSummaryStrings } from 'common/strings/plan-summary-strings'
import {
  GA4AddToCart,
  GA4AddShippingInfo,
  GA4AddPaymentInfo,
  GA4Item,
  GA4Purchase,
} from '../../types/global'
import { TOrder } from 'common/types/order-type'
import { coupon } from 'common/useCalculatePrice'
import { getTenantCurrency } from 'common/tenant'

// Helper function to get price based on duration
const getPrice = (
  pricing: { month_price: number; year_price: number },
  duration: TDuration,
) => {
  return duration === 'month' ? pricing.month_price : pricing.year_price
}

// Helper function to get price ID based on duration
const getPriceId = (
  pricing: { month_price_id: string; year_price_id: string },
  duration: TDuration,
) => {
  return duration === 'month' ? pricing.month_price_id : pricing.year_price_id
}

// Helper function to build GA4 item from plan or addon
const buildGA4Item = (
  data: TPlanSummary | TAddon,
  duration: TDuration,
  includePrice = false,
  quantity = 1,
): GA4Item => {
  const itemName = 'shortName' in data ? data.shortName : data.title
  const item: GA4Item = {
    // Will be set below if includePrice is true
    currency: getTenantCurrency(),

    item_id: getPriceId(data.pricing, duration),

    item_name: `${planSummaryStrings[itemName]} ${duration}`,
    price: '',
    quantity: quantity || 1,
  }

  if (includePrice) {
    item.price = (getPrice(data.pricing, duration) / 100).toFixed(2)
  }

  return item
}

// Shared function to build items array for GA4 events
// This ensures orderToEvent and shippingInfoToEvent track addons identically
const buildGA4ItemsArray = (
  plan: TPlanSummary,
  addons: TAddon[],
  duration: TDuration,
) => {
  return [
    {
      item_id: getPriceId(plan.pricing, duration),
      item_name: `${planSummaryStrings[plan.shortName]} ${duration}`,
    },
    ...addons.map((addon) => ({
      item_id: getPriceId(addon.pricing, duration),
      item_name: `${planSummaryStrings[addon.title]} ${duration}`,
    })),
  ]
}

export const planToEvent = (
  data: TPlanSummary,
  duration: TDuration,
  buttonType?: string,
) => {
  const price = getPrice(data.pricing, duration)
  return {
    currency: getTenantCurrency(),
    items: [buildGA4Item(data, duration, true, 1)],
    value: (price / 100).toFixed(2),
    ...(buttonType && { button_type: buttonType }),
  } as GA4AddToCart
}

export const orderToEvent = (
  data: TPlanSummary,
  addons: TAddon[],
  duration: TDuration,
  order: TOrder,
  additionalPlanSummaries?: TPlanSummary[],
  directorsCount?: number,
) => {
  // Use shared items array and add price + quantity for purchase event
  const items = buildGA4ItemsArray(data, addons, duration).map((item) => ({
    ...item,
    price: '', // Price is calculated per item below
    quantity: 1,
  }))

  // Add individual prices to each item
  items[0].price = (getPrice(data.pricing, duration) / 100).toFixed(2)
  addons.forEach((addon, index) => {
    if (
      addon.slug === AddonSlug.DIRECTORS_SERVICE_ADDRESS &&
      directorsCount &&
      directorsCount > 0
    ) {
      // Use per-director pricing and quantity from the addon's API pricing
      const directorPrice = getPrice(addon.pricing, duration) / 100
      items[index + 1].item_id = getPriceId(addon.pricing, duration)
      items[index + 1].price = directorPrice.toFixed(2)
      items[index + 1].quantity = directorsCount
    } else {
      items[index + 1].price = (
        getPrice(addon.pricing, duration) / 100
      ).toFixed(2)
    }
  })

  // Add additional plans (e.g. WhatsApp for Business Number bought alongside a Virtual Office plan)
  if (additionalPlanSummaries && additionalPlanSummaries.length > 0) {
    additionalPlanSummaries.forEach((plan) => {
      items.push({
        item_id: getPriceId(plan.pricing, duration),
        item_name: `${planSummaryStrings[plan.shortName]} ${duration}`,
        price: (getPrice(plan.pricing, duration) / 100).toFixed(2),
        quantity: 1,
      })
    })
  }

  return {
    currency: getTenantCurrency(),
    items,
    tax: (order.tax / 100).toFixed(2),
    transaction_id: `${order.order_id}`,
    value: (order.sub_total / 100).toFixed(2),
  } as GA4Purchase
}

export const shippingInfoToEvent = (
  data: TPlanSummary,
  addons: TAddon[],
  duration: TDuration,
) => {
  // Calculate the sum of (price * quantity) for all items (excluding VAT/tax)
  const planPrice = getPrice(data.pricing, duration)
  const addonsPrice = addons.reduce(
    (sum, addon) => sum + getPrice(addon.pricing, duration),
    0,
  )
  const totalValue = (planPrice + addonsPrice) / 100

  return {
    currency: getTenantCurrency(),
    ...(coupon?.code && { coupon: coupon.code }),
    items: buildGA4ItemsArray(data, addons, duration),
    value: totalValue.toFixed(2),
  } as GA4AddShippingInfo
}

export const paymentInfoToEvent = (
  data: TPlanSummary,
  addons: TAddon[],
  duration: TDuration,
) => {
  // Calculate the sum of (price * quantity) for all items (excluding VAT/tax)
  const planPrice = getPrice(data.pricing, duration)
  const addonsPrice = addons.reduce(
    (sum, addon) => sum + getPrice(addon.pricing, duration),
    0,
  )
  const totalValue = (planPrice + addonsPrice) / 100

  return {
    currency: getTenantCurrency(),
    ...(coupon?.code && { coupon: coupon.code }),
    items: buildGA4ItemsArray(data, addons, duration),
    value: totalValue.toFixed(2),
  } as GA4AddPaymentInfo
}
